import React from 'react';
import { Order } from './OrderPlanning';
import { Clock3Icon, CheckCircleIcon, AlertCircleIcon, CheckIcon, XIcon, EditIcon, ClipboardListIcon, GripIcon } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
interface OrderListProps {
  orders: Order[];
  onReorder: (reorderedOrders: Order[]) => void;
}
export function OrderList({
  orders,
  onReorder
}: OrderListProps) {
  const getStatusBadge = (status: Order['status']) => {
    switch (status) {
      case 'planned':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1129">
            <Clock3Icon size={12} className="mr-1" data-id="element-1130" />
            Geplant
          </span>;
      case 'in-progress':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1131">
            <Clock3Icon size={12} className="mr-1" data-id="element-1132" />
            In Bearbeitung
          </span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1133">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1134" />
            Abgeschlossen
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1135">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-1136" />
            Storniert
          </span>;
    }
  };
  const handleDragEnd = (result: any) => {
    // Wenn kein Ziel oder gleiche Position, nichts tun
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    // Neue Reihenfolge der Aufträge erstellen
    const reorderedOrders = Array.from(orders);
    const [movedOrder] = reorderedOrders.splice(result.source.index, 1);
    reorderedOrders.splice(result.destination.index, 0, movedOrder);
    // Callback an übergeordnete Komponente
    onReorder(reorderedOrders);
  };
  if (orders.length === 0) {
    return <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1137">
        <ClipboardListIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-1138" />
        <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-1139">
          Keine Aufträge geplant
        </h3>
        <p className="text-gray-500" data-id="element-1140">
          Für diesen Tag sind noch keine Produktionsaufträge angelegt.
        </p>
      </div>;
  }
  return <DragDropContext onDragEnd={handleDragEnd} data-id="element-1141">
      <div className="overflow-hidden border border-gray-200 rounded-lg" data-id="element-1142">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1143">
          <thead className="bg-gray-50" data-id="element-1144">
            <tr data-id="element-1145">
              <th scope="col" className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-1146">
                <span className="sr-only" data-id="element-1147">Drag Handle</span>
              </th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20" data-id="element-1148">
                Position
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1149">
                Auftrag
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1150">
                Rezept
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1151">
                Menge
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1152">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1153">
                Aktionen
              </th>
            </tr>
          </thead>
          <Droppable droppableId="orders-table" direction="vertical" data-id="element-1154">
            {provided => <tbody {...provided.droppableProps} ref={provided.innerRef} className="bg-white divide-y divide-gray-200" data-id="element-1155">
                {orders.map((order, index) => <Draggable key={order.id} draggableId={order.id} index={index} isDragDisabled={order.status === 'completed' || order.status === 'cancelled'} data-id="element-1156">
                    {(provided, snapshot) => <tr ref={provided.innerRef} {...provided.draggableProps} className={`${snapshot.isDragging ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1157">
                        <td className="px-2 py-4 whitespace-nowrap text-center" {...provided.dragHandleProps} data-id="element-1158">
                          <GripIcon size={16} className="inline-block text-gray-400" data-id="element-1159" />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center" data-id="element-1160">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1161">
                          #{order.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1162">
                          <div className="flex flex-col" data-id="element-1163">
                            <span data-id="element-1164">{order.recipeName}</span>
                            <span className="text-xs text-gray-400" data-id="element-1165">
                              {order.recipeId}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1166">
                          {order.quantity} {order.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1167">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1168">
                          <div className="flex justify-end space-x-2" data-id="element-1169">
                            <button className="text-blue-600 hover:text-blue-900" title="Bearbeiten" data-id="element-1170">
                              <EditIcon size={16} data-id="element-1171" />
                            </button>
                            {order.status === 'in-progress' && <button className="text-green-600 hover:text-green-900" title="Abschließen" data-id="element-1172">
                                <CheckIcon size={16} data-id="element-1173" />
                              </button>}
                            {(order.status === 'planned' || order.status === 'in-progress') && <button className="text-red-600 hover:text-red-900" title="Stornieren" data-id="element-1174">
                                <XIcon size={16} data-id="element-1175" />
                              </button>}
                          </div>
                        </td>
                      </tr>}
                  </Draggable>)}
                {provided.placeholder}
              </tbody>}
          </Droppable>
        </table>
      </div>
    </DragDropContext>;
}